// FNP-1193 — mirrors the FNP-1221 schema
// (flexi-domains-api/src/appsync-api/src/schema/features/certificates.graphql).
//
//   listCertificates(limit: Int, nextToken: String): CertificateConnection
//   getCertificate(id: ID!): Certificate
//   requestCertificate(input: RequestCertificateInput!): Certificate
//   deleteCertificate(input: DeleteCertificateInput!): DeleteCertificateResult
//
// Five rules travel with these types and the code below enforces them:
//
//  1. `status` and `expirationDate` are NOT STORED server-side. They are read
//     through to the provider on every request. Nothing here may cache them,
//     derive a second copy, or treat a previously-seen status as current.
//  2. `STUCK` and `VALIDATION_NOT_WRITTEN` are DIFFERENT STATES with different
//     fixes. Neither is a spinner. See `certificateStatusCopy`.
//  3. A wildcard matches EXACTLY ONE LABEL. `*.example.com` does not cover
//     `a.b.example.com`. See `wildcardCoverageNote`.
//  4. `targetType` decides the region. `us-east-1` is CLOUDFRONT's rule and
//     applies to no other member.
//  5. The provider is the authority on delete. `inUse` is a distinguishable
//     refusal, not a generic error.

// WHERE A CERTIFICATE IS HEADED, which is what decides its region.
//
// ⚠️ THE TARGET IS AN ATTRIBUTE, NOT A PREMISE. An earlier draft of this
// feature asserted certificates are issued in us-east-1 because they serve
// CloudFront; the owner rejected that. The target may be a distribution in the
// tenant's own account, an ALB, an API Gateway, or nothing at all beyond a
// management view. Region follows what the certificate serves.
export type CertificateTargetType =
    | 'CLOUDFRONT'
    | 'LOAD_BALANCER'
    | 'API_GATEWAY'
    | 'UNSPECIFIED';

// ⚠️ FLEXI'S VOCABULARY, NOT ACM'S. `STUCK` has no ACM counterpart — it is
// computed on demand from how long validation has been outstanding.
export type CertificateStatus =
    | 'ISSUED'
    | 'PENDING_VALIDATION'
    | 'STUCK'
    | 'VALIDATION_NOT_WRITTEN'
    | 'FAILED'
    | 'REVOKED'
    | 'EXPIRED'
    | 'UNKNOWN';

// The DNS record the provider wants published to prove domain control.
//
// Flexi writes this itself through the owning zone's adapter, so a tenant
// should never copy it by hand — but when validation is STUCK this is the first
// thing anyone wants to read, which is why it is rendered rather than hidden.
export type CertificateValidationRecord = {
    name: string,
    // Record type, uppercase. ACM uses CNAME.
    type: string,
    value: string,
    // The hostname on the certificate this record validates. A certificate
    // covering several names has one per name and they settle independently,
    // which is why this is a list rather than a single record.
    domainName: string,
    // The provider's own view of THIS name's validation, verbatim.
    status?: string | null,
}

export type Certificate = {
    // ⚠️ FLEXI'S OWN ID, NEVER THE ARN. Composite and tenant-scoped:
    // "CERTIFICATE#<accountId>#<uuid>".
    id: string,
    // ⚠️ THE CONSOLE Account.id UUID, not an AWS account number. See
    // `issuingAwsAccountId` below and docs/API_ENTITY_SEAM_MAP.md §2.
    accountId: string,
    connectionId: string,
    domainName: string,
    // Every name this certificate covers, primary first.
    coveredNames: string[],
    targetType: CertificateTargetType,
    region: string,

    // ── READ THROUGH TO THE PROVIDER. NOT STORED. ────────────────────────
    status: CertificateStatus,
    // Verbatim from the provider, so a human can see what it actually said.
    statusDetail?: string | null,
    expirationDate?: string | null,
    validationRecords: CertificateValidationRecord[],

    // ── REQUEST-TIME FACTS. STORED. ──────────────────────────────────────
    //
    // ⚠️ THE CLOCK `STUCK` IS MEASURED FROM, and NOT the provider's own
    // CreatedAt. NULL means Flexi never recorded writing the record, which
    // resolves to VALIDATION_NOT_WRITTEN rather than STUCK.
    validationRecordWrittenAt?: string | null,
    validationZoneId?: string | null,
    stuckAfterMinutes: number,
    // ⚠️ THE AWS 12-DIGIT ACCOUNT NUMBER, named so on purpose — `accountId`
    // above is the console UUID. Conflating them is this platform's most
    // expensive recurring bug class.
    issuingAwsAccountId?: string | null,
    createdByUsername?: string | null,
    created: string,
    updated: string,
}

export type CertificateConnectionResult = {
    items: Certificate[],
    nextToken: string | null,
}

export type ListCertificates = {
    listCertificates: {
        items: Certificate[],
        nextToken?: string | null,
    }
}

export type GetCertificate = {
    getCertificate?: Certificate | null
}

// What deleting a certificate answered with.
//
// ⚠️ THE PROVIDER IS THE AUTHORITY. `inUse` carries its refusal back
// distinguishably, so the console can say "this certificate is serving
// something" rather than "something went wrong" — different fixes.
export type DeleteCertificateResult = {
    id: string,
    connectionId: string,
    // Whether the provider actually deleted it.
    deleted: boolean,
    // True ONLY when the refusal was BECAUSE THE CERTIFICATE IS IN USE.
    inUse: boolean,
    // The provider's own message, verbatim, when it refused.
    reason?: string | null,
    // Whether Flexi's local index row was removed. A refusal leaves it.
    localRecordDeleted: boolean,
}

export type DeleteCertificate = {
    deleteCertificate?: DeleteCertificateResult | null
}

export type RequestCertificate = {
    requestCertificate?: Certificate | null
}

export type RequestCertificateInput = {
    connectionId: string,
    domainName: string,
    subjectAlternativeNames?: string[],
    targetType: CertificateTargetType,
    // ⚠️ REQUIRED FOR A REGIONAL TARGET, IGNORED FOR CLOUDFRONT — and supplying
    // a different region for a CloudFront target is REFUSED rather than
    // silently overridden, so a caller who believes they chose eu-west-1 is
    // told they did not.
    region?: string,
    stuckAfterMinutes?: number,
}

export type DeleteCertificateInput = {
    id: string,
}

// ─── THE STATUS VOCABULARY, AS A HUMAN READS IT ───
//
// ⚠️ FOUR STATES THAT LOOK ALIKE AND ARE NOT. The whole point of the backend's
// status computation is that a pending certificate has three distinct
// meanings, and flattening them back into one spinner here would discard
// exactly what FNP-1221 went out of its way to preserve.

export type CertificateStatusTone = 'positive' | 'progressing' | 'attention' | 'terminal' | 'unknown';

export type CertificateStatusCopy = {
    label: string,
    tone: CertificateStatusTone,
    // What this state MEANS, and what to do about it. Rendered where there is
    // room for a sentence — the row tooltip and the detail panel.
    detail: string,
    // Whether this state is waiting on something that will resolve itself.
    // Drives whether the row invites action.
    settles: boolean,
}

// Not a module-scope object literal: the strings go through I18n at CALL time,
// after the language is configured. A literal captures the default locale at
// import.
export const certificateStatusCopy = (
    status: CertificateStatus,
    translate: (key: string) => string,
): CertificateStatusCopy => {

    switch (status) {

        case 'ISSUED':
            return {
                label: translate('Issued'),
                tone: 'positive',
                detail: translate('This certificate is usable now.'),
                settles: false,
            };

        // Progressing normally, still inside the threshold.
        case 'PENDING_VALIDATION':
            return {
                label: translate('Pending validation'),
                tone: 'progressing',
                detail: translate('The provider is checking the validation record. This normally settles within a few minutes and needs nothing from you.'),
                settles: true,
            };

        // ⚠️ A DISTINCT STATE, NOT A SPINNER. A silent pending that lasts days
        // is otherwise indistinguishable from a broken feature — which is the
        // whole reason this member exists.
        case 'STUCK':
            return {
                label: translate('Validation stuck'),
                tone: 'attention',
                detail: translate('The validation record was written, but the provider has not confirmed it within the expected time. Check the validation record below against what is actually published in the zone — a mismatch, or a record removed after it was written, is the usual cause.'),
                settles: false,
            };

        // ⚠️ A DIFFERENT AND MORE ACTIONABLE PROBLEM THAN STUCK, and it does
        // NOT age into STUCK. Nothing is waiting on DNS propagation here: the
        // record was never written at all. "Wait longer" is the wrong advice.
        case 'VALIDATION_NOT_WRITTEN':
            return {
                label: translate('Validation record not written'),
                tone: 'attention',
                detail: translate('{product} has no record of writing the validation record into the zone, so nothing is waiting on DNS propagation — the write itself did not happen. This is not a matter of waiting: the record below needs to be published before validation can start.'),
                settles: false,
            };

        case 'FAILED':
            return {
                label: translate('Failed'),
                tone: 'terminal',
                detail: translate('The provider gave up on this certificate. It cannot recover — request a new one.'),
                settles: false,
            };

        // Terminal, and deliberately distinct from FAILED because both describe
        // a certificate that once worked.
        case 'REVOKED':
            return {
                label: translate('Revoked'),
                tone: 'terminal',
                detail: translate('This certificate was revoked. It once worked and no longer does.'),
                settles: false,
            };

        case 'EXPIRED':
            return {
                label: translate('Expired'),
                tone: 'terminal',
                detail: translate('This certificate has passed its expiry date and no longer serves traffic.'),
                settles: false,
            };

        // ⚠️ NAMED RATHER THAN COERCED TO FAILED. Claiming a certificate failed
        // when the provider simply grew a status this schema does not model is
        // a lie the console would act on.
        default:
            return {
                label: translate('Unknown'),
                tone: 'unknown',
                detail: translate('The provider returned a status this console does not recognise. This is not a claim that anything is wrong — read the provider\'s own message below.'),
                settles: false,
            };
    }
};

// The Mantine colour for a tone. One mapping, so two components cannot disagree
// about what "attention" looks like.
export const statusToneColor = (tone: CertificateStatusTone): string => {
    switch (tone) {
        case 'positive':
            return 'green';
        case 'progressing':
            return 'blue';
        case 'attention':
            return 'orange';
        case 'terminal':
            return 'red';
        default:
            return 'gray';
    }
};

// ⚠️ THE TWO PENDING-BUT-NOT-PROGRESSING STATES, as one predicate.
//
// Both need an operator to look at something; neither resolves by waiting.
// Callers that want "does this row need attention" ask this rather than
// re-listing the members and drifting apart from each other.
export const needsAttention = (certificate: Certificate): boolean =>
    certificate.status === 'STUCK' || certificate.status === 'VALIDATION_NOT_WRITTEN';

// ─── THE WILDCARD CONSTRAINT ───

/**
 * Whether a hostname is a wildcard name.
 */
export const isWildcard = (name: string): boolean => name.startsWith('*.');

/**
 * How many labels deep a hostname sits below its registrable parent, counting
 * from the wildcard's perspective.
 *
 * ⚠️ THIS IS THE FACT THE TICKET ASKS TO EXPLAIN RATHER THAN HIDE.
 *
 * A wildcard matches EXACTLY ONE label. `*.example.com` covers `www.example.com`
 * and does NOT cover `a.b.example.com`. That is a fact about how certificates
 * work, not an argument against the hierarchy someone wants — so this exists to
 * word the explanation, never to block the request.
 */
export const labelCount = (name: string): number =>
    name.split('.')
        .filter(label => label !== '').length;

/**
 * Whether `wildcard` actually covers `hostname`.
 *
 * Exported for its own test: the one-label rule is the whole point and testing
 * it through a rendered form would test the plumbing instead of the rule.
 */
export const wildcardCovers = (wildcard: string, hostname: string): boolean => {

    if (!isWildcard(wildcard)) {
        // A literal name covers only itself.
        return wildcard.toLowerCase() === hostname.toLowerCase();
    }

    const parent = wildcard.slice(2)
                           .toLowerCase();
    const candidate = hostname.toLowerCase();

    if (!candidate.endsWith(`.${parent}`)) {
        return false;
    }

    // ⚠️ EXACTLY ONE LABEL. The prefix left after removing the parent must
    // contain no dot of its own — `a.b` is two labels and is NOT covered.
    const prefix = candidate.slice(0, candidate.length - parent.length - 1);
    return prefix !== '' && !prefix.includes('.');
};

/**
 * The explanation to show at the point in the form where it applies.
 *
 * Returns undefined when there is nothing worth saying, so a caller renders
 * nothing rather than a permanent lecture. The note appears when the operator
 * is typing a name deep enough for the constraint to bite — which is exactly
 * where the ticket asks for it, and nowhere else.
 */
export const wildcardCoverageNote = (
    domainName: string,
    translate: (key: string) => string,
): string | undefined => {

    const name = domainName.trim();

    if (name === '') {
        return undefined;
    }

    if (isWildcard(name)) {
        // A wildcard being requested: say what it will and will not cover, so
        // an operator expecting it to cover everything beneath finds out now
        // rather than after a browser shows a certificate error.
        const parent = name.slice(2);
        return translate('A wildcard covers exactly one label. This certificate will cover names like www.')
            + parent
            + translate(', but NOT a name two levels down such as a.b.')
            + parent
            + translate('. To cover a deeper name, add it explicitly as an additional name below — that is supported and is the right way to do it.');
    }

    // A literal name three or more labels deep. This is the case the ticket
    // names: the operator may believe a parent wildcard already covers it.
    //
    // ⚠️ NOT A WARNING AND NOT A BLOCK. Requesting the deeper name directly is
    // the CORRECT action, and this says so — the awkwardness is a fact about
    // certificates, not a reason to talk anyone out of their hierarchy.
    if (labelCount(name) >= 4) {
        return translate('This name is more than one label below its parent, so a wildcard certificate for the parent does not cover it. Requesting it directly, as you are doing here, is the right way to give it a certificate.');
    }

    return undefined;
};
